<template>
  <q-page class="incorp-page">
    <div class="incorp-container">
      <!-- Loading state -->
      <template v-if="loading">
        <div>
          <q-skeleton type="text" width="200px" dark style="font-size: 1.75rem;" />
          <q-skeleton type="text" width="280px" dark style="margin-top: 4px;" />
        </div>
        <q-skeleton type="rect" height="56px" dark style="border-radius: 12px; margin-top: 16px;" />
        <q-skeleton type="rect" height="200px" dark style="border-radius: 12px; margin-top: 16px;" />
        <q-skeleton type="rect" height="120px" dark style="border-radius: 12px; margin-top: 16px;" />
      </template>

      <!-- Error state -->
      <div v-else-if="error" class="incorp-empty">
        <q-icon name="error_outline" size="48px" style="color: var(--oa-danger);" />
        <p class="empty-text">{{ error }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          style="background: var(--oa-gold-full); color: var(--color-marble-cream); font-weight: 600; border-radius: 8px; margin-top: 16px;"
          @click="fetchData"
        />
      </div>

      <!-- No incorporation active -->
      <div v-else-if="!incorp" class="incorp-empty">
        <div class="incorp-empty-icon">
          <q-icon name="school" size="36px" style="color: var(--oa-gold-dim);" />
        </div>
        <h2 class="incorp-empty-title">Tu camino comienza pronto</h2>
        <p class="incorp-empty-desc">
          Todavia no tenes un proceso de incorporacion asignado.
          Contacta a tu Senior Coach o al OT de tu sede para arrancar.
        </p>
      </div>

      <!-- Loaded state -->
      <template v-else>
        <!-- Header -->
        <div class="incorp-header">
          <h1 class="incorp-title">Mi Incorporacion</h1>
          <p class="incorp-subtitle">
            {{ incorp.sede?.name || 'Sin sede' }} &middot; Mentor: {{ mentorName }}
          </p>
        </div>

        <!-- Progress bar -->
        <div class="incorp-progress-card">
          <div class="incorp-progress-header">
            <span class="incorp-progress-label">
              <template v-if="activePhaseIdx >= PHASES.length">
                Incorporacion completada
              </template>
              <template v-else>
                Fase {{ activePhaseIdx + 1 }} de {{ PHASES.length }} &mdash; {{ PHASES[activePhaseIdx]?.label }}
              </template>
            </span>
            <span class="incorp-progress-pct">{{ overallPct }}%</span>
          </div>
          <div class="incorp-progress-track">
            <div
              class="incorp-progress-fill"
              :style="{ width: overallPct + '%' }"
            />
          </div>
        </div>

        <!-- Phase timeline -->
        <div class="incorp-phases">
          <div v-for="(phase, i) in PHASES" :key="phase.key">
            <!-- Phase header button -->
            <button
              class="incorp-phase-btn"
              :class="{
                'phase-locked': i > activePhaseIdx,
                'phase-active': i === activePhaseIdx,
              }"
              :style="phaseHeaderStyle(phase, i)"
              :disabled="i > activePhaseIdx"
              @click="togglePhase(phase.key, i)"
            >
              <div class="phase-icon-wrap" :style="phaseIconStyle(phase, i)">
                <q-icon
                  :name="i < activePhaseIdx ? 'check_circle' : i > activePhaseIdx ? 'lock' : phase.icon"
                  size="16px"
                />
              </div>
              <div class="phase-label-col">
                <p class="phase-label-title" :style="{ color: i > activePhaseIdx ? 'var(--oa-text-disabled)' : 'var(--oa-text-primary)' }">
                  Fase {{ i + 1 }}: {{ phase.label }}
                </p>
                <p class="phase-label-desc">{{ phase.desc }}</p>
              </div>
              <q-badge
                v-if="i < activePhaseIdx"
                label="Completada"
                style="background: color-mix(in srgb, var(--oa-success) 15%, transparent); color: var(--oa-success); font-size: 10px; padding: 2px 8px; border-radius: 6px;"
              />
              <q-badge
                v-else-if="i === activePhaseIdx"
                label="En curso"
                style="background: color-mix(in srgb, var(--oa-gold-full) 15%, transparent); color: var(--oa-gold-full); font-size: 10px; padding: 2px 8px; border-radius: 6px;"
              />
              <q-icon
                v-if="i <= activePhaseIdx"
                :name="expandedPhase === phase.key ? 'expand_less' : 'expand_more'"
                size="16px"
                style="color: var(--oa-text-muted); flex-shrink: 0;"
              />
            </button>

            <!-- Phase content (expanded) -->
            <div
              v-if="expandedPhase === phase.key && i <= activePhaseIdx"
              class="incorp-phase-content"
            >
              <!-- Phase 0: Welcome -->
              <div v-if="phase.key === 'welcome'" class="phase-welcome">
                <q-icon name="school" size="40px" style="color: var(--oa-gold-full);" />
                <p class="phase-welcome-title">Bienvenido a El Templo</p>
                <p class="phase-welcome-desc">
                  Tu proceso de incorporacion tiene {{ PHASES.length }} fases.
                  Completa cada una para convertirte en Coach certificado de El Templo.
                </p>
              </div>

              <!-- Phase 1: Cultura checklist -->
              <template v-if="phase.key === 'i1_cultura'">
                <div class="checklist-header">
                  <span class="checklist-count">{{ checklistCompleted }}/{{ checklistKeys.length }} items completados</span>
                  <q-badge
                    v-if="incorp.checklist?.verifiedAt"
                    label="Verificado por mentor"
                    style="background: color-mix(in srgb, var(--oa-success) 15%, transparent); color: var(--oa-success); font-size: 10px; padding: 2px 8px; border-radius: 6px;"
                  />
                </div>
                <div class="checklist-grid">
                  <div
                    v-for="key in checklistKeys"
                    :key="key"
                    class="checklist-item"
                    :style="{
                      background: isChecklistChecked(key)
                        ? 'color-mix(in srgb, var(--oa-success) 8%, transparent)'
                        : 'var(--oa-bg-3)',
                      color: isChecklistChecked(key) ? 'var(--oa-success)' : 'var(--oa-text-muted)',
                    }"
                  >
                    <q-icon
                      :name="isChecklistChecked(key) ? 'check_circle' : 'menu_book'"
                      size="14px"
                    />
                    <span class="checklist-item-label">{{ CHECKLIST_LABELS[key] }}</span>
                  </div>
                </div>
              </template>

              <!-- Phase 2: Guia Operativa -->
              <template v-if="phase.key === 'guia'">
                <div class="checklist-header">
                  <span class="checklist-count">
                    {{ guideProgress.approved }}/{{ guideProgress.total }} situaciones aprobadas ({{ guideProgress.pct }}%)
                  </span>
                  <q-badge
                    :label="'Gate: 80%'"
                    :style="{
                      background: 'var(--oa-bg-3)',
                      color: guideProgress.pct >= 80 ? 'var(--oa-success)' : 'var(--oa-text-disabled)',
                      fontSize: '10px',
                      padding: '2px 8px',
                      borderRadius: '6px',
                    }"
                  />
                </div>
                <q-linear-progress
                  :value="guideProgress.pct / 100"
                  :color="guideProgress.pct >= 80 ? 'green' : 'amber'"
                  track-color="grey-9"
                  rounded
                  size="6px"
                />
                <q-btn
                  no-caps
                  unelevated
                  label="Ir a la Guia Operativa"
                  icon-right="open_in_new"
                  style="background: color-mix(in srgb, var(--oa-tier-sc) 8%, transparent); color: var(--oa-tier-sc); font-size: 12px; font-weight: 500; border-radius: 8px; margin-top: 12px;"
                  :to="{ name: 'guia' }"
                />
              </template>

              <!-- Phase 3: Observations -->
              <template v-if="phase.key === 'i2_observacion'">
                <div class="checklist-header">
                  <span class="checklist-count">{{ incorp.observations?.length ?? 0 }}/10 observaciones</span>
                  <q-icon
                    v-if="(incorp.observations?.length ?? 0) >= 10"
                    name="check_circle"
                    size="16px"
                    style="color: var(--oa-success);"
                  />
                </div>
                <q-linear-progress
                  :value="Math.min((incorp.observations?.length ?? 0) / 10, 1)"
                  color="amber"
                  track-color="grey-9"
                  rounded
                  size="6px"
                />
                <div v-if="incorp.observations && incorp.observations.length > 0" class="obs-list">
                  <div
                    v-for="obs in incorp.observations"
                    :key="obs.id"
                    class="obs-item"
                  >
                    <span class="obs-seq">#{{ obs.sequenceNumber }}</span>
                    <span class="obs-date">{{ formatDate(obs.sessionDate) }}</span>
                    <span class="obs-format">{{ obs.format }}</span>
                    <span v-if="obs.blocksIdentified" class="obs-blocks">
                      {{ (obs.blocksIdentified as string[]).map((b: string) => BLOCK_LABELS[b] ?? b).join(', ') }}
                    </span>
                    <q-icon
                      :name="obs.mentorReviewed ? 'check_circle' : 'schedule'"
                      size="12px"
                      :style="{ color: obs.mentorReviewed ? 'var(--oa-success)' : 'var(--oa-text-disabled)', marginLeft: 'auto', flexShrink: 0 }"
                    />
                  </div>
                </div>
                <p v-else class="phase-no-data">Sin observaciones registradas aun</p>
              </template>

              <!-- Phase 4: Co-directions -->
              <template v-if="phase.key === 'i3_codireccion'">
                <div class="checklist-header">
                  <span class="checklist-count">{{ incorp.codirections?.length ?? 0 }}/5 co-direcciones</span>
                  <q-icon
                    v-if="(incorp.codirections?.length ?? 0) >= 5"
                    name="check_circle"
                    size="16px"
                    style="color: var(--oa-success);"
                  />
                </div>
                <q-linear-progress
                  :value="Math.min((incorp.codirections?.length ?? 0) / 5, 1)"
                  color="amber"
                  track-color="grey-9"
                  rounded
                  size="6px"
                />
                <div v-if="incorp.codirections && incorp.codirections.length > 0" class="codir-list">
                  <div
                    v-for="cod in incorp.codirections"
                    :key="cod.id"
                    class="codir-card"
                  >
                    <div class="codir-header">
                      <span class="codir-session">
                        Sesion #{{ cod.sequenceNumber }} &mdash; {{ formatDate(cod.sessionDate) }}
                      </span>
                      <span class="codir-format">{{ cod.format }}</span>
                    </div>
                    <div v-if="cod.blocksLed" class="codir-blocks">
                      <span
                        v-for="b in (cod.blocksLed as string[])"
                        :key="b"
                        class="codir-block-badge"
                      >
                        {{ BLOCK_LABELS[b] ?? b }}
                      </span>
                    </div>
                    <div class="codir-scores">
                      <div
                        v-for="dim in [
                          { label: 'Tec', score: cod.scoreTecnica },
                          { label: 'Int', score: cod.scoreInteraccion },
                          { label: 'Amb', score: cod.scoreAmbiente },
                          { label: 'Ins', score: cod.scoreIntensidad },
                          { label: 'Est', score: cod.scoreEstructura },
                          { label: 'Pro', score: cod.scoreProgreso },
                        ]"
                        :key="dim.label"
                        class="codir-score-cell"
                      >
                        <p class="codir-score-label">{{ dim.label }}</p>
                        <p
                          class="codir-score-value"
                          :style="{
                            color: dim.score && dim.score >= 4 ? 'var(--oa-success)' : dim.score && dim.score >= 3 ? 'var(--oa-gold-full)' : '#ef4444',
                          }"
                        >
                          {{ dim.score ?? '&mdash;' }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <p v-else class="phase-no-data">Sin co-direcciones registradas aun</p>
              </template>

              <!-- Phase 5: First solo session -->
              <template v-if="phase.key === 'i4_primera_solo'">
                <template v-if="incorp.firstSessionEvals && incorp.firstSessionEvals.length > 0">
                  <div
                    v-for="ev in incorp.firstSessionEvals"
                    :key="ev.id"
                    class="eval-card"
                  >
                    <div class="eval-header">
                      <span class="eval-date">
                        {{ formatDate(ev.sessionDate) }}
                        <template v-if="ev.sessionDurationMin"> &mdash; {{ ev.sessionDurationMin }} min</template>
                      </span>
                      <q-badge
                        :label="ev.result === 'aprobado' ? 'Aprobado' : ev.result === 'desaprobado' ? 'Desaprobado' : 'Pendiente'"
                        :style="{
                          background: ev.result === 'aprobado'
                            ? 'color-mix(in srgb, var(--oa-gold-full) 15%, transparent)'
                            : 'var(--oa-bg-3)',
                          color: ev.result === 'aprobado' ? 'var(--oa-gold-full)' : 'var(--oa-text-secondary)',
                          fontSize: '10px',
                          padding: '2px 8px',
                          borderRadius: '6px',
                        }"
                      />
                    </div>
                    <div class="eval-dims">
                      <div
                        v-for="dim in [
                          { label: 'Tecnica', weighted: ev.weightedTecnica, weight: '30%' },
                          { label: 'Interaccion', weighted: ev.weightedInteraccion, weight: '20%' },
                          { label: 'Ambiente', weighted: ev.weightedAmbiente, weight: '15%' },
                          { label: 'Intensidad', weighted: ev.weightedIntensidad, weight: '15%' },
                          { label: 'Estructura', weighted: ev.weightedEstructura, weight: '10%' },
                          { label: 'Progreso', weighted: ev.weightedProgreso, weight: '10%' },
                        ]"
                        :key="dim.label"
                        class="eval-dim-cell"
                        :style="evalDimStyle(dim.weighted)"
                      >
                        <p class="eval-dim-label">{{ dim.label }} ({{ dim.weight }})</p>
                        <p class="eval-dim-value">{{ dim.weighted ?? '&mdash;' }}</p>
                      </div>
                    </div>
                    <div class="eval-total">
                      <p class="eval-total-label">Score Total</p>
                      <p class="eval-total-value">{{ ev.totalScore }}</p>
                    </div>
                  </div>
                </template>
                <div v-else class="phase-welcome">
                  <q-icon name="military_tech" size="32px" style="color: var(--oa-text-disabled);" />
                  <p class="phase-no-data">Evaluacion pendiente de programacion</p>
                </div>
              </template>
            </div>
          </div>

          <!-- Completed state -->
          <div v-if="incorp.currentPhase === 'completado'" class="incorp-completed">
            <q-icon name="check_circle" size="32px" style="color: var(--oa-success);" />
            <p class="incorp-completed-title">Incorporacion Completada</p>
            <p class="incorp-completed-desc">
              Sos Coach de El Templo. Tu camino de certificacion continua en El Ascenso.
            </p>
            <q-btn
              no-caps
              unelevated
              label="Ir a El Ascenso"
              icon-right="chevron_right"
              style="background: var(--oa-gold-full); color: var(--color-marble-cream); font-weight: 600; font-size: 12px; border-radius: 8px; margin-top: 12px;"
              :to="{ name: 'ascenso' }"
            />
          </div>
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { api } from 'boot/axios';

// ---------- Types ----------
interface ChecklistData {
  verifiedAt?: string | null;
  [key: string]: boolean | string | null | undefined;
}

interface Observation {
  id: string;
  sequenceNumber: number;
  sessionDate: string;
  format: string;
  blocksIdentified?: string[];
  notes?: string;
  mentorReviewed?: boolean;
}

interface Codirection {
  id: string;
  sequenceNumber: number;
  sessionDate: string;
  format: string;
  blocksLed?: string[];
  blocksObserved?: string[];
  feedbackEcr?: string;
  scoreTecnica?: number;
  scoreInteraccion?: number;
  scoreAmbiente?: number;
  scoreIntensidad?: number;
  scoreEstructura?: number;
  scoreProgreso?: number;
}

interface FirstSessionEval {
  id: string;
  sessionDate: string;
  sessionDurationMin?: number;
  result: string;
  totalScore?: string;
  weightedTecnica?: string;
  weightedInteraccion?: string;
  weightedAmbiente?: string;
  weightedIntensidad?: string;
  weightedEstructura?: string;
  weightedProgreso?: string;
}

interface Incorporation {
  id: string;
  currentPhase: string;
  status: string;
  mentor?: { displayName?: string; firstName?: string };
  sede?: { name: string };
  checklist?: ChecklistData;
  observations?: Observation[];
  codirections?: Codirection[];
  firstSessionEvals?: FirstSessionEval[];
  i1StartedAt?: string;
}

interface GuideProgress {
  total: number;
  approved: number;
  pct: number;
  gatePassed: boolean;
}

// ---------- Constants ----------
const PHASES = [
  { key: 'welcome', label: 'Bienvenida', desc: 'Tu camino de incorporacion', icon: 'local_fire_department', accent: 'var(--oa-gold-full)' },
  { key: 'i1_cultura', label: 'Cultura y Operacion', desc: 'Induccion cultural: leyes, protocolos, pilares', icon: 'menu_book', accent: 'var(--oa-tier-cc)' },
  { key: 'guia', label: 'Guia Operativa', desc: '28 situaciones reales que debes dominar (80% min)', icon: 'description', accent: 'var(--oa-tier-sc)' },
  { key: 'i2_observacion', label: 'Observacion Activa', desc: '10+ sesiones observando a tu mentor', icon: 'visibility', accent: 'var(--oa-tier-c1)' },
  { key: 'i3_codireccion', label: 'Co-Direccion', desc: '5+ sesiones co-dirigidas con feedback', icon: 'groups', accent: 'var(--oa-tier-c2)' },
  { key: 'i4_primera_solo', label: 'Primera Sesion Solo', desc: 'Sesion HICT evaluada con rubrica hexagonal', icon: 'military_tech', accent: 'var(--oa-tier-ot)' },
];

const CHECKLIST_LABELS: Record<string, string> = {
  leyesOperativas: 'Recitar las 5 Leyes Operativas',
  ritualApertura: 'Ritual de Apertura de sesion',
  frasesImpacto: 'Frases de impacto (4 estilos)',
  protocoloAntiCringe: 'Protocolo Anti-Cringe',
  crmKommo: 'CRM Kommo (leads)',
  crmDeportnet: 'CRM DeportNet (asistencia)',
  protocoloRecepcion: 'Protocolo de Recepcion',
  entrenamientoDescalzo: 'Entrenamiento Descalzo',
  fistBump: 'Fist Bump protocolo',
  musicaPorHorario: 'Musica por Horario',
  contingenciaSinProfe: 'Contingencia sin Profesor',
  pilaresCulturales: 'Recitar los 8 Pilares Culturales',
};

const BLOCK_LABELS: Record<string, string> = {
  cars: 'CARS', initium: 'Initium', nucleus: 'Nucleus',
  deuteros: 'Deuteros', athlos: 'Athlos', epikos: 'Epikos',
};

const checklistKeys = Object.keys(CHECKLIST_LABELS);

// ---------- State ----------
const loading = ref(true);
const error = ref<string | null>(null);
const incorp = ref<Incorporation | null>(null);
const expandedPhase = ref<string | null>(null);
const guideProgress = ref<GuideProgress>({ total: 0, approved: 0, pct: 0, gatePassed: false });

// ---------- Computed ----------
const mentorName = computed(() => {
  if (!incorp.value?.mentor) return 'Sin asignar';
  return incorp.value.mentor.displayName || incorp.value.mentor.firstName || 'Sin asignar';
});

const checklistCompleted = computed(() => {
  const cl = incorp.value?.checklist;
  if (!cl) return 0;
  return checklistKeys.filter((k) => cl[k] === true).length;
});

const activePhaseIdx = computed(() => {
  if (!incorp.value) return 0;
  const phaseMap: Record<string, number> = {
    i1_cultura: 1, i2_observacion: 3, i3_codireccion: 4, i4_primera_solo: 5, completado: 6,
  };
  const currentDbPhase = phaseMap[incorp.value.currentPhase] ?? 1;
  let idx = 0;
  if (currentDbPhase >= 1) idx = 1;

  const checklistDone = checklistCompleted.value === checklistKeys.length && incorp.value.checklist?.verifiedAt;
  if (checklistDone) idx = 2;
  if (guideProgress.value.gatePassed && checklistDone) idx = Math.max(idx, currentDbPhase >= 3 ? 3 : 2);
  if (currentDbPhase >= 3) idx = 3;
  if (currentDbPhase >= 4) idx = 4;
  if (currentDbPhase >= 5) idx = 5;
  if (incorp.value.currentPhase === 'completado') idx = 6;
  return idx;
});

const overallPct = computed(() => {
  const weights = [5, 10, 35, 20, 20, 10];
  let pct = 0;
  for (let i = 0; i < PHASES.length; i++) {
    if (i < activePhaseIdx.value) {
      pct += weights[i];
    } else if (i === activePhaseIdx.value) {
      if (i === 1) pct += weights[i] * (checklistCompleted.value / checklistKeys.length);
      else if (i === 2) pct += weights[i] * (guideProgress.value.pct / 100);
      else if (i === 3) pct += weights[i] * Math.min((incorp.value?.observations?.length ?? 0) / 10, 1);
      else if (i === 4) pct += weights[i] * Math.min((incorp.value?.codirections?.length ?? 0) / 5, 1);
    }
  }
  return Math.round(pct);
});

// ---------- Helpers ----------
function isChecklistChecked(key: string): boolean {
  return incorp.value?.checklist?.[key] === true;
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '--';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function phaseHeaderStyle(phase: typeof PHASES[number], i: number) {
  const isActive = i === activePhaseIdx.value;
  const isCompleted = i < activePhaseIdx.value;
  return {
    background: isActive
      ? `color-mix(in srgb, ${phase.accent} 6%, var(--oa-bg-2))`
      : 'var(--oa-bg-2)',
    border: `1px solid ${isActive ? phase.accent : isCompleted ? 'var(--oa-success)' : 'var(--oa-border)'}`,
    boxShadow: isActive ? `0 0 16px color-mix(in srgb, ${phase.accent} 8%, transparent)` : undefined,
  };
}

function phaseIconStyle(phase: typeof PHASES[number], i: number) {
  const isCompleted = i < activePhaseIdx.value;
  const isActive = i === activePhaseIdx.value;
  return {
    background: isCompleted
      ? 'color-mix(in srgb, var(--oa-success) 15%, transparent)'
      : isActive
        ? `color-mix(in srgb, ${phase.accent} 12%, transparent)`
        : 'var(--oa-bg-3)',
    color: isCompleted ? 'var(--oa-success)' : isActive ? phase.accent : 'var(--oa-text-disabled)',
  };
}

function evalDimStyle(weighted: string | undefined) {
  const w = parseFloat(weighted ?? '0');
  return {
    background: w >= 8
      ? 'color-mix(in srgb, var(--oa-success) 8%, transparent)'
      : w >= 6
        ? 'color-mix(in srgb, var(--oa-gold-full) 8%, transparent)'
        : 'color-mix(in srgb, #ef4444 8%, transparent)',
    border: `1px solid ${w >= 8 ? 'var(--oa-success)' : w >= 6 ? 'var(--oa-gold-dim)' : '#ef4444'}`,
  };
}

function togglePhase(key: string, i: number) {
  if (i > activePhaseIdx.value) return;
  expandedPhase.value = expandedPhase.value === key ? null : key;
}

// ---------- Data Fetching ----------
async function fetchData() {
  loading.value = true;
  error.value = null;
  try {
    const { data } = await api.get('/incorporacion/mine');
    incorp.value = data;

    if (data) {
      // Auto-expand active phase
      const idx = activePhaseIdx.value;
      expandedPhase.value = PHASES[Math.min(idx, PHASES.length - 1)]?.key ?? null;

      // Fetch guide progress
      try {
        const { data: gp } = await api.get('/guide/progress');
        guideProgress.value = gp;
      } catch {
        // Guide progress not critical
      }
    }
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Error al cargar incorporacion';
    error.value = msg;
  } finally {
    loading.value = false;
  }
}

// ---------- Lifecycle ----------
onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.incorp-page {
  padding: 24px;
}

.incorp-container {
  max-width: 760px;
  margin: 0 auto;
}

.incorp-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: 12px;
}

.empty-text {
  font-size: 13px;
  color: var(--oa-text-muted);
}

.incorp-empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--oa-gold-full) 8%, transparent);
}

.incorp-empty-title {
  font-size: 20px;
  font-weight: 700;
  font-family: var(--oa-font-display);
  color: var(--oa-text-primary);
  margin: 0;
}

.incorp-empty-desc {
  font-size: 13px;
  color: var(--oa-text-secondary);
  text-align: center;
  max-width: 360px;
  margin: 0;
}

.incorp-header {
  margin-bottom: 16px;
}

.incorp-title {
  font-size: 28px;
  font-weight: 600;
  font-style: italic;
  font-family: var(--oa-font-display);
  color: var(--oa-text-primary);
  margin: 0;
}

.incorp-subtitle {
  font-size: 13px;
  color: var(--oa-text-muted);
  margin: 4px 0 0;
}

/* Progress bar */
.incorp-progress-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.incorp-progress-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.incorp-progress-label {
  font-size: 11px;
  font-weight: 500;
  color: var(--oa-text-muted);
}

.incorp-progress-pct {
  font-size: 11px;
  font-family: monospace;
  font-weight: 700;
  color: var(--oa-gold-full);
}

.incorp-progress-track {
  width: 100%;
  height: 10px;
  border-radius: 9999px;
  background: var(--oa-bg-3);
}

.incorp-progress-fill {
  height: 10px;
  border-radius: 9999px;
  background: linear-gradient(90deg, var(--oa-tier-cc), var(--oa-gold-full));
  transition: width 0.7s ease;
}

/* Phase timeline */
.incorp-phases {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.incorp-phase-btn {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  background: none;
  font: inherit;
}

.incorp-phase-btn.phase-locked {
  opacity: 0.4;
  cursor: not-allowed;
}

.phase-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.phase-label-col {
  flex: 1;
  min-width: 0;
}

.phase-label-title {
  font-size: 13px;
  font-weight: 600;
  margin: 0;
}

.phase-label-desc {
  font-size: 11px;
  color: var(--oa-text-muted);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Phase content */
.incorp-phase-content {
  margin-top: 4px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.phase-welcome {
  text-align: center;
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.phase-welcome-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.phase-welcome-desc {
  font-size: 12px;
  color: var(--oa-text-muted);
  max-width: 400px;
  margin: 0;
}

.phase-no-data {
  font-size: 12px;
  color: var(--oa-text-muted);
  text-align: center;
  margin: 0;
}

/* Checklist */
.checklist-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.checklist-count {
  font-size: 11px;
  font-weight: 500;
  color: var(--oa-text-muted);
}

.checklist-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

@media (max-width: 640px) {
  .checklist-grid {
    grid-template-columns: 1fr;
  }
}

.checklist-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 11px;
}

.checklist-item-label {
  flex: 1;
}

/* Observations */
.obs-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 192px;
  overflow-y: auto;
}

.obs-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 11px;
  background: var(--oa-bg-3);
}

.obs-seq {
  font-family: monospace;
  color: var(--oa-text-disabled);
}

.obs-date {
  color: var(--oa-text-primary);
}

.obs-format {
  color: var(--oa-text-muted);
}

.obs-blocks {
  font-size: 10px;
  color: var(--oa-gold-mid);
}

/* Co-directions */
.codir-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 240px;
  overflow-y: auto;
}

.codir-card {
  padding: 12px;
  border-radius: 8px;
  background: var(--oa-bg-3);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.codir-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
}

.codir-session {
  font-weight: 500;
  color: var(--oa-text-primary);
}

.codir-format {
  color: var(--oa-text-disabled);
}

.codir-blocks {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.codir-block-badge {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  background: color-mix(in srgb, var(--oa-gold-full) 10%, transparent);
  color: var(--oa-gold-full);
}

.codir-scores {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 4px;
}

.codir-score-cell {
  text-align: center;
}

.codir-score-label {
  font-size: 9px;
  color: var(--oa-text-disabled);
  margin: 0;
}

.codir-score-value {
  font-size: 12px;
  font-weight: 700;
  margin: 0;
}

/* Eval */
.eval-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.eval-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.eval-date {
  font-size: 11px;
  color: var(--oa-text-muted);
}

.eval-dims {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.eval-dim-cell {
  padding: 8px;
  border-radius: 8px;
  text-align: center;
}

.eval-dim-label {
  font-size: 10px;
  color: var(--oa-text-muted);
  margin: 0;
}

.eval-dim-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.eval-total {
  text-align: center;
  padding: 12px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--oa-gold-full) 6%, transparent);
}

.eval-total-label {
  font-size: 10px;
  color: var(--oa-text-muted);
  margin: 0;
}

.eval-total-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--oa-gold-full);
  margin: 0;
}

/* Completed */
.incorp-completed {
  margin-top: 8px;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  background: color-mix(in srgb, var(--oa-success) 6%, transparent);
  border: 1px solid var(--oa-success);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.incorp-completed-title {
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.incorp-completed-desc {
  font-size: 12px;
  color: var(--oa-text-muted);
  margin: 0;
}
</style>
